import * as Popover from '@radix-ui/react-popover';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Separator } from '~/components';
import { Button } from '~/components/Button';
import { Checkbox } from '~/components/Checkbox';
import { chevron } from '~/components/styles';
import { cn } from '~/lib/cn';

interface PlatformsPickerProps {
  value: string[];
  onApply: (value: string[]) => void;
}

const PLATFORMS = ['facebook', 'tiktok', 'snapchat', 'twitter'];

export function PlatformsPicker({ onApply, value }: PlatformsPickerProps) {
  const { t } = useTranslation('campaign-planner', {
    keyPrefix: 'benchmarks',
  });
  const { t: nameTranslation } = useTranslation('names');

  // States
  const [open, setOpen] = useState(false);
  const [pendingPlatforms, setPendingPlatforms] = useState<string[]>(value);

  useEffect(() => {
    if (!open) {
      setPendingPlatforms(value);
    }
  }, [open, value]);

  const togglePlatform = (platform: string) => {
    setPendingPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform],
    );
  };

  const handleApplyButtonClick = () => {
    onApply(pendingPlatforms);
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          aria-label="platforms-picker-trigger"
          className="relative flex w-full items-center gap-sm rounded-sm border py-sm pl-md pr-2xl transition-colors duration-100 bg-emphasis-default border-emphasis-subtle text-inverse hover:bg-emphasis-strong"
        >
          <span className="text-white">
            {value.length
              ? t('platforms.platformsSummary', {
                  count: value.length,
                })
              : 'Select platforms'}
          </span>
          <Icon
            className={cn(chevron({ state: open ? 'open' : 'default' }))}
            size="md"
            type="chevron-down"
          />
        </button>
      </Popover.Trigger>

      <Popover.Content
        side="bottom"
        align="start"
        className="z-10 mt-xs w-[--radix-popover-trigger-width] rounded-sm border bg-level-03 border-emphasis-subtle"
      >
        <div className="flex flex-col">
          {PLATFORMS.map((platform) => {
            const isSelected = pendingPlatforms.includes(platform);

            return (
              <div
                tabIndex={0}
                key={platform}
                role="checkbox"
                aria-checked={isSelected}
                aria-label={platform}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    togglePlatform(platform);
                  }
                }}
                onClick={(e) => {
                  e.preventDefault();
                  togglePlatform(platform);
                }}
                className={cn(
                  'flex cursor-pointer items-center justify-between gap-xs px-md py-[0.75rem] first:rounded-t-sm hover:bg-emphasis-default focus:outline-none focus:bg-level-02',
                  isSelected ? 'text-strong' : 'text-subtle',
                )}
              >
                <div className="flex items-center gap-sm text-body-sm">
                  <Checkbox
                    className="tracking-wide"
                    checked={isSelected}
                    tabIndex={-1}
                  />
                  {nameTranslation(platform)}
                </div>
              </div>
            );
          })}
        </div>

        <Separator />

        <div className="flex justify-end gap-md px-lg py-md font-semibold">
          <Button
            variant="tertiary"
            onClick={() => {
              setOpen(false);
            }}
          >
            {t('labels.cancel')}
          </Button>
          <Button className="flex px-md py-sm" onClick={handleApplyButtonClick}>
            {t('labels.apply')}
          </Button>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

PlatformsPicker.displayName = 'PlatformsPicker';
